import { 
  doc, 
  getDoc, 
  setDoc, 
  collection,
  query,
  getDocs,
  limit
} from 'firebase/firestore';
import { db, isFirebaseConfigured } from '../config/firebase';

export interface FounderData {
  name: string;
  picture: string;
  title: string;
  bio: string;
}

const FOUNDER_DOC_ID = 'founder_info'; // Single document ID for founder data

/**
 * Check if error is an offline/network error
 */
const isOfflineError = (error: any): boolean => {
  return error?.code === 'unavailable' || 
         error?.code === 'failed-precondition' ||
         error?.message?.includes('offline') ||
         error?.message?.includes('network');
};

/**
 * Check if error is a permission error
 */
const isPermissionError = (error: any): boolean => {
  return error?.code === 'permission-denied' ||
         error?.code === 'unauthenticated' ||
         error?.message?.includes('permission');
};

/**
 * Get founder data from Firestore
 * Falls back to localStorage if Firestore is not configured or fails
 */
export const getFounderData = async (): Promise<FounderData | null> => {
  // Check if Firebase is configured
  if (!isFirebaseConfigured() || !db) {
    // Fallback to localStorage
    const savedData = localStorage.getItem('founder_data');
    if (savedData) {
      try {
        return JSON.parse(savedData);
      } catch (e) {
        console.error('Error parsing localStorage data:', e);
      }
    }
    return null;
  }

  try {
    const founderRef = doc(db, 'founder', FOUNDER_DOC_ID);
    const founderSnap = await getDoc(founderRef);

    if (founderSnap.exists()) {
      const data = founderSnap.data() as FounderData;
      // Also save to localStorage as cache
      localStorage.setItem('founder_data', JSON.stringify(data));
      return data;
    } else {
      // If document doesn't exist, try localStorage
      const savedData = localStorage.getItem('founder_data');
      if (savedData) {
        try {
          return JSON.parse(savedData);
        } catch (e) {
          console.error('Error parsing localStorage data:', e);
        }
      }
      return null;
    }
  } catch {
    // Fallback to localStorage
    const savedData = localStorage.getItem('founder_data');
    if (savedData) {
      try {
        return JSON.parse(savedData);
      } catch (e) {
        console.error('Error parsing localStorage data:', e);
      }
    }
    return null;
  }
};

/**
 * Save founder data to Firestore
 * Always saves to localStorage first, then tries Firebase
 * Returns success status and message
 */
export const saveFounderData = async (data: FounderData): Promise<{ success: boolean; message: string }> => {
  // Always save to localStorage first (this always works)
  localStorage.setItem('founder_data', JSON.stringify(data));
  
  // Check if Firebase is configured
  if (!isFirebaseConfigured() || !db) {
    return {
      success: true,
      message: 'Saved locally. Firebase is not configured. Please set up your Firebase credentials in .env file.'
    };
  }
  
  try {
    const founderRef = doc(db, 'founder', FOUNDER_DOC_ID);
    await setDoc(founderRef, data, { merge: true });
    
    return {
      success: true,
      message: 'Founder information updated successfully and synced to all devices!'
    };
  } catch (error: any) {
    // Check if it's an offline error
    if (isOfflineError(error)) {
      return {
        success: true,
        message: 'Saved locally. Changes will sync to all devices when you\'re back online.'
      };
    }
    
    // Check if it's a permission error
    if (isPermissionError(error)) {
      return {
        success: true,
        message: 'Saved locally. Permission denied by Firestore. Please check your Firestore security rules to allow writes.'
      };
    }
    
    // Other errors: data saved locally, sync failed
    return {
      success: true,
      message: 'Saved locally. Unable to sync to Firebase. Please check your connection and Firebase configuration.'
    };
  }
};

